import React, { useImperativeHandle, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Upload, X } from 'lucide-react';
import { useAuth } from '@/hooks/domains/auth/useAuth';
import ImageUpload, {
  ImageUploadHandle,
} from '@/components/dashboard/profile/image-upload/ImageUpload';
import { cn } from '@/lib/utils';

export interface AddImageCardHandle {
  openFilePicker: () => void;
}

interface AddImageCardProps {
  onImageUrlChange: (urls: string[]) => void;
  onCancel: () => void;
  onUploadStatusChange?: (uploading: boolean) => void;
  onPreview?: (previewUrls: string[]) => void;
  variant?: 'full' | 'compact';
  className?: string;
}

const AddImageCard = React.forwardRef<AddImageCardHandle, AddImageCardProps>(
  ({
    onImageUrlChange,
    onCancel,
    onUploadStatusChange,
    onPreview,
    variant,
    className,
  }, ref) => {
    const { user } = useAuth();
    const imageUploadRef = useRef<ImageUploadHandle>(null);
    const [isUploading, setIsUploading] = React.useState(false);

    useImperativeHandle(ref, () => ({
      openFilePicker: () => {
        if (imageUploadRef.current) {
          imageUploadRef.current.openFilePicker();
        }
      }
    }));

    const handleCardClick = () => {
      if (imageUploadRef.current) {
        imageUploadRef.current.openFilePicker();
      }
    };

    const handleUploadingChange = (uploading: boolean) => {
      setIsUploading(uploading);
      if (onUploadStatusChange) {
        onUploadStatusChange(uploading);
      }
    };

    const isCompact = variant === 'compact';
    const isFull = variant === 'full';

    const cardClass = cn(
      'relative flex items-center justify-center transition-colors',
      {
        'border-2 border-dashed border-gray-300 rounded-xl cursor-pointer bg-transparent': isFull || isCompact,
        'p-0': isFull || isCompact,
        'h-full hover:bg-muted/50': isFull || isCompact,
        'border-2 border-dashed border-gray-300 rounded-xl pt-4 pr-4 pl-4 pb-2': !variant,
      },
      className
    );
    
    const contentClass = cn('flex items-center justify-center text-center', {
      'p-4 gap-2 flex-col': isFull || isCompact,
      'gap-3 w-full flex-row': !variant,
      'flex-col md:flex-row': isUploading && !variant,
    });

    const iconClass = cn('text-muted-foreground', {
      'h-4 w-4': isFull,
      'h-5 w-5': isCompact,
      'h-7 w-7': !variant,
    });

    const titleClass = cn('font-semibold', {
      'text-base': isFull,
      'text-sm': isCompact,
      'text-base text-foreground text-[#111827]': !variant,
    });
    
    const descClass = cn('font-medium text-muted-foreground', {
      'text-xs md:w-2/3 text-[#6B7280]': isFull,
      'text-xs text-[#6B7280]': isCompact,
      'text-sm': !variant,
    });

    const getUploadIcon = () => {
      if (!variant) {
        return <Upload className={cn(iconClass, 'h-7 w-7')} />;
      }
      return <Upload className={iconClass} />;
    }

    return (
      <Card
        className={cardClass}
        onClick={handleCardClick}
        tabIndex={0}
        role="button"
        aria-label="Upload files"
      >
        {(isFull) && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onCancel();
            }}
            className="absolute top-4 right-4 text-muted-foreground hover:text-foreground z-10 p-1 rounded-full hover:bg-muted"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        )}
        <CardContent className={contentClass + (!variant ? ' w-full' : '')}>
          {(isFull || isCompact) && (
            <div className="flex flex-col items-center justify-center gap-2 pointer-events-none select-none">
              <div className="bg-[#E6E6E9] rounded-full p-2 mb-2">
                {getUploadIcon()}
              </div>
              <div className={titleClass}>Upload files</div>
              <div className={descClass}>
                <span className="font-semibold text-[#6B7280] text-foreground">
                  Click to browse or drag here
                </span>
                {isFull && (
                  " Upload up to 20 images (JPEG, PNG, GIF, or WEBP), max 5MB each. Aim for 1600×1600px+ and use 1:1\" or 16:9."
                )}
              </div>
            </div>
          )}
          {!variant && (
            <div className="flex flex-row items-center gap-3 w-full pointer-events-none select-none">
              <div className="bg-muted rounded-lg p-1 flex items-center justify-center">
                {getUploadIcon()}
              </div>
              <div className="flex flex-col items-center md:items-start justify-center md:pr-2">
                <div className={titleClass}>Upload files</div>
                <div className={descClass}>Click to browse or drag here</div>
              </div>
            </div>
          )}
          <ImageUpload 
            ref={imageUploadRef}
            user={user}
            bucketName="portfolio-images"
            onImageUploaded={onImageUrlChange}
            onPreview={onPreview}
            label=""
            buttonText={(isCompact || !variant) && isUploading ? 'Upload files' : undefined}
            previewSize="large"
            aspectRatio="wide"
            showPreview={false}
            multiple={true}
            hideLocalStorageWarning={isUploading || isCompact || !variant}
            onUploadStatusChange={handleUploadingChange}
          />
          {/* Uploading indicator at the bottom, only for isFull */}
          {isUploading && isFull && (
            <div className="flex items-center gap-2 text-muted-foreground mt-4 justify-center w-full">
              <svg className="animate-spin h-5 w-5 text-gray-400" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              Uploading...
            </div>
          )}
        </CardContent>
      </Card>
    );
  }
);

export default AddImageCard;
